import { AuthCard } from '@authentication/components/AuthCard';

import useLoginMutation from '@authentication/mutations/loginMutation';
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoginRequest from '@authentication/models/login.request.dto';
import TextFieldForm from '@atoms/TextFieldForm';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import {
  ButtonLogin,
  ErrorLoginTypography,
  LabelField,
} from './login-card.styled';
import { Select } from '@atoms/Select';
import { useGetStores } from '@/queries/storesQueries';
import Loading from '@atoms/Loading';
import useUser from '@authentication/hooks/useUser';
import React, { useMemo } from 'react';

const initialValues = {
  username: '',
  password: '',
  storeId: undefined,
};

const loginSchema = Yup.object().shape({
  username: Yup.string().email('invalid email').required('required'),
  password: Yup.string().required('required'),
  storeId: Yup.number().required('required'),
});

const LoginCard = () => {
  const { t } = useTranslation();
  const { mutate: loginMutate, isError } = useLoginMutation();
  const { data: storesData, isLoading: storesLoading } = useGetStores();
  const { pageTitle, setPageTitle } = useUser();
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    setPageTitle('Suivez vos candidats et gérez vos espaces.');
  }, [pageTitle]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => event.preventDefault();

  const methods = useForm<LoginRequest>({
    mode: 'onChange',
    defaultValues: useMemo(() => initialValues, [initialValues]),
    resolver: yupResolver(loginSchema) as any,
  });

  const { formState, handleSubmit, setValue } = methods;
  const { isDirty } = formState;

  const onSubmit = handleSubmit((data) => {
    loginMutate(data);
  });
  if (storesLoading) return <Loading />;

  return (
    <AuthCard title={t('CONNECTION_TO_PROFILE')}>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} autoComplete="off">
          <Stack gap={1}>
            <LabelField>Store</LabelField>
            <Select
              variant="outlined"
              withBorder
              options={
                storesData?.data.items?.map((item: any) => ({
                  label: item.name,
                  value: String(item.id),
                })) ?? []
              }
              onChange={(e) => {
                setValue('storeId', Number(e.target.value));
              }}
            />

            <LabelField>Adresse mail ou nom d’utilisateur</LabelField>
            <TextFieldForm
              name="username"
              fullWidth
              noBorder
              placeholder={'Email' ?? ''}
            />
            <LabelField>Mot de passe</LabelField>

            <OutlinedInput
              name="password"
              placeholder="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              required
              sx={{
                paddingBlock: '8px',
                paddingRight: '16px',
              }}
              fullWidth
              onChange={(e) => {
                setValue('password', e.target.value);
              }}
              size="small"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />

            {isError && (
              <ErrorLoginTypography>
                e-mail ou mot de passe incorrect
              </ErrorLoginTypography>
            )}
            <ButtonLogin disabled={!isDirty} type="submit">
              {t('CONNECT')}
            </ButtonLogin>
            <Typography
              variant="subtitle2"
              sx={{ color: 'gray', textAlign: 'center' }}
            >
              {process.env.REACT_APP_VERSION}
            </Typography>
          </Stack>
        </form>
      </FormProvider>
    </AuthCard>
  );
};

export default LoginCard;
